import { BackToLists } from '../components/BackToLists'
import { ScreenHead } from '../components/ScreenHead'
import { ShortlistBody } from '../components/ShortlistBody'
import { Banner, Button } from '../ui'
import { useLeads, useVenues } from '../lib/queries'
import { useDismissLead, usePromoteLead } from '../lib/mutations'
import { useWriteError } from '../lib/useAction'
import { fmt } from '../lib/calendar'
import { localVenueMatch } from '../lib/leadVenue'
import { PROMOTE_TYPES } from '../lib/leadPromote'
import { useIsAdmin, adminGate } from '../lib/useIsAdmin'
import type { Lead } from '../lib/types'
import { Loading, Failed } from './Stub'

/**
 * The Shortlist — WP 4.3's rename of what was "Saved": a head and
 * `ShortlistBody`. The body is its own component so the Lists index can mount
 * the same thing inline. On the phone this screen is no longer routed:
 * `#/lists/shortlist` is rewritten to the index, which draws the body itself
 * (`screens/Lists.tsx`). The desk still mounts this as its Shortlist pane,
 * and a link copied from there (`lib/deskList.ts`) lands on the phone's index.
 *
 * `only="leads"` is the other view this file has always carried — the
 * newsletters' open leads, which share a venue lookup and a promote path with
 * nothing in the body, so they are drawn here and not there.
 */
export function Shortlist({ only = 'saved', onBack }: { only?: 'saved' | 'leads'; onBack?: () => void }) {
  // U0.3: leads are an admin queue (promoting one writes `entries`; U0.2).
  // Nothing routes here with `only="leads"` today; a non-admin who somehow
  // arrives gets the Shortlist, not an empty queue they cannot act on.
  const isAdmin = useIsAdmin()
  if (only === 'leads' && adminGate(isAdmin) === 'show') return <LeadsView onBack={onBack} />
  return (
    <>
      {onBack && <BackToLists onBack={onBack} />}
      {/* WP 10.7: no `ScreenHead` at all before this — 10.1 moved the account
          button out of the wordmark bar into each tab's own head, and this
          route (reached from Lists, not a tab) never got one. */}
      <ScreenHead lead={<h1 className="screen-title">Shortlist</h1>} />
      <ShortlistBody variant="screen" />
    </>
  )
}

function LeadsView({ onBack }: { onBack?: () => void }) {
  const leadsQ = useLeads()
  const venuesQ = useVenues()
  const promoteM = usePromoteLead()
  const dismissLeadM = useDismissLead()
  const writeError = useWriteError(promoteM, dismissLeadM)

  const leads = leadsQ.data ?? []
  const venues = venuesQ.data?.list ?? []
  const title = <h1 className="screen-title">Leads</h1>
  const error = leadsQ.error ?? venuesQ.error
  if (leadsQ.isPending || venuesQ.isPending) return <><ScreenHead lead={title} /><Loading /></>
  if (error) return <><ScreenHead lead={title} /><Failed error={error.message} /></>

  const leadRow = (l: Lead) => {
    const x = l.extracted
    const place = localVenueMatch(x?.place_guess ?? null, venues)
    const dates = x?.dates ?? []
    const when = dates.length === 0 ? 'No date given'
      : dates.length === 1 ? fmt(dates[0], { weekday: 'short', day: 'numeric', month: 'short' })
      : `${fmt(dates[0], { day: 'numeric', month: 'short' })} – ${fmt(dates[dates.length - 1], { day: 'numeric', month: 'short' })}`

    return (
      <div key={l.id} className="leadrow">
        <div className="leadrow__head">
          <b>{x?.title ?? 'Untitled'}</b>
          <span className="g">{when}</span>
        </div>
        <div className="g leadrow__where">
          {x?.place_guess ?? 'no place stated'}
          {place ? ` · known as ${place.name}` : x?.place_guess ? ' · not in the frame' : ''}
          {l.mail_source ? ` · via ${l.mail_source.split('@')[1] ?? l.mail_source}` : ''}
        </div>
        <details className="leadrow__text">
          <summary>What the newsletter said</summary>
          <p>{l.text}</p>
        </details>
        <div className="leadrow__actions">
          {PROMOTE_TYPES.map(t => (
            <Button key={t.type} size="sm" variant="primary"
                    onClick={() => promoteM.mutate({ lead: l, type: t.type, venueId: place?.id ?? null })}>
              {t.label}
            </Button>
          ))}
          <Button size="sm" variant="quiet" onClick={() => dismissLeadM.mutate(l.id)}>Not for me</Button>
          {x?.url && (
            <a className="linkbtn" href={x.url} target="_blank" rel="noopener noreferrer">Site</a>
          )}
        </div>
      </div>
    )
  }

  return (
    <>
      {onBack && <BackToLists onBack={onBack} />}
      <ScreenHead lead={title} />
      {writeError && <Banner tone="bad">{writeError}</Banner>}

      {leads.length === 0 ? (
        <div className="empty">No leads waiting. The newsletters have nothing the adapters miss.</div>
      ) : (
        <>
          <p className="note">
            What the newsletters found that no adapter covers. Promoting one puts it in the
            feed; dismissing it closes it for good. They expire on their own after 30 days.
          </p>
          {leads.map(leadRow)}
        </>
      )}
    </>
  )
}
